import { Helmet } from 'react-helmet-async'
import { useState, useEffect } from 'react'
import { db, supabase } from '../lib/supabase'
import { useRecaptcha } from '../hooks/useRecaptcha'

export default function TrabajaConNosotros() {
  const { verifyRecaptcha } = useRecaptcha()
  const [formData, setFormData] = useState({
    nombres: '',
    telefono: '',
    email: '',
    cargo: '',
    cv: null
  })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [seoConfig, setSeoConfig] = useState(null)
  const [faqs, setFaqs] = useState([])

  useEffect(() => {
    loadSEOConfig()
    loadFAQs()
  }, [])

  const loadSEOConfig = async () => {
    const { data } = await db.getSEOConfig('trabaja')
    setSeoConfig(data || null)
  }

  const loadFAQs = async () => {
    const { data } = await db.getFAQsByScope('page', null, 'trabaja')
    setFaqs(data || [])
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    
    if (!file) {
      setFormData({ ...formData, cv: null })
      setError('')
      return
    }

    // Validate file type
    if (file.type !== 'application/pdf') {
      setError('Solo se permiten archivos PDF')
      e.target.value = ''
      return
    }

    // Validate file size (2MB max)
    if (file.size > 2097152) {
      setError('El archivo no debe pesar más de 2MB')
      e.target.value = ''
      return
    }

    setError('')
    setFormData({ ...formData, cv: file })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Verify reCAPTCHA
      const recaptchaResult = await verifyRecaptcha('trabaja_con_nosotros')
      if (!recaptchaResult.success) {
        throw new Error('Error de verificación. Por favor intenta de nuevo.')
      }

      if (!formData.cv) {
        setError('Debes subir tu CV en PDF')
        setLoading(false)
        return
      }

      // Generate unique file name
      const fileExt = 'pdf'
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`
      const filePath = `cvs/${fileName}`

      // Upload CV to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('postulaciones')
        .upload(filePath, formData.cv, {
          contentType: 'application/pdf',
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        throw new Error('Error al subir el CV')
      }

      // Create postulacion record
      const postulacionData = {
        nombres: formData.nombres,
        telefono: formData.telefono || null,
        email: formData.email,
        cargo: formData.cargo,
        cv_path: filePath,
        cv_mime: 'application/pdf',
        cv_size_bytes: formData.cv.size,
        origen: 'postulacion'
      }

      const { error: dbError } = await db.createPostulacion(postulacionData)

      if (dbError) {
        console.error('DB error:', dbError)
        // If DB insert fails, try to delete the uploaded file
        await supabase.storage.from('postulaciones').remove([filePath])
        throw new Error('Error al guardar la postulación')
      }

      setSuccess(true)
      setFormData({
        nombres: '',
        telefono: '',
        email: '',
        cargo: '',
        cv: null
      })
      // Reset file input
      document.getElementById('cv-input').value = ''
    } catch (err) {
      console.error('Error:', err)
      setError(err.message || 'Error al enviar la postulación. Por favor intenta nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>{seoConfig?.meta_title || 'Trabaja con Nosotros - Plaza Aurora'}</title>
        <meta name="description" content={seoConfig?.meta_description || 'Únete al equipo de Plaza Aurora Outlet. Envíanos tu CV y forma parte de nuestro equipo.'} />
        <link rel="canonical" href={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/trabaja`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_PE" />
        <meta property="og:site_name" content="Plaza Aurora" />
        <meta property="og:url" content={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/trabaja`} />
        <meta property="og:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Trabaja con Nosotros - Plaza Aurora'} />
        <meta property="og:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Únete al equipo de Plaza Aurora Outlet. Envíanos tu CV y forma parte de nuestro equipo.'} />
        {seoConfig?.og_image_url && <meta property="og:image" content={seoConfig.og_image_url} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={seoConfig?.twitter_card || 'summary_large_image'} />
        <meta name="twitter:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Trabaja con Nosotros - Plaza Aurora'} />
        <meta name="twitter:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Únete al equipo de Plaza Aurora Outlet. Envíanos tu CV y forma parte de nuestro equipo.'} />
        {seoConfig?.og_image_url && <meta name="twitter:image" content={seoConfig.og_image_url} />}
        
        {/* Robots */}
        <meta name="robots" content={seoConfig?.robots_index === false ? 'noindex,nofollow' : 'index,follow'} />
      </Helmet>
      
      {seoConfig?.json_ld && (
        <script type="application/ld+json">
          {seoConfig.json_ld}
        </script>
      )}
      
      {/* Hero Section */}
      <div style={{
        background: 'var(--color-black-block)',
        color: '#FFFFFF',
        padding: '4rem 0',
        textAlign: 'center',
        marginTop: '4.5rem'
      }}>
        <div style={{
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: '700',
            fontFamily: 'var(--font-display)',
            marginBottom: '1rem',
            lineHeight: '1.2',
            letterSpacing: '0.02em'
          }}>TRABAJA CON NOSOTROS</h1>
          <p style={{
            fontSize: 'var(--font-size-h3)',
            fontFamily: 'var(--font-ui)',
            opacity: 0.95
          }}>Únete al equipo de Plaza Aurora Outlet</p>
        </div>
      </div>

      {/* Form Section */}
      <div style={{
        maxWidth: 'var(--container-max-width)',
        margin: '0 auto',
        padding: '4rem 2rem'
      }}>
        <div style={{
          maxWidth: '672px',
          margin: '0 auto'
        }}>
          {success ? (
            <div style={{
              background: '#D1FAE5',
              color: '#065F46',
              padding: '2rem',
              borderRadius: 'var(--radius-md)',
              textAlign: 'center'
            }}>
              <h2 style={{
                fontSize: 'var(--font-size-h2)',
                fontWeight: '700',
                fontFamily: 'var(--font-display)',
                marginBottom: '0.5rem'
              }}>¡GRACIAS POR POSTULAR!</h2>
              <p style={{
                fontSize: 'var(--font-size-body)',
                fontFamily: 'var(--font-ui)'
              }}>Hemos recibido tu postulación. Nos contactaremos contigo pronto.</p>
              <button
                onClick={() => setSuccess(false)}
                style={{
                  marginTop: '1.5rem',
                  padding: '0.75rem 2rem',
                  background: 'var(--color-text-primary)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-body)',
                  fontWeight: '500',
                  fontFamily: 'var(--font-ui)',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Enviar otra postulación
              </button>
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: '2rem'
            }}>
              <h2 style={{
                fontSize: 'var(--font-size-h2)',
                fontWeight: '700',
                fontFamily: 'var(--font-display)',
                color: 'var(--color-text-primary)',
                marginBottom: '0.5rem',
                textAlign: 'center'
              }}>FORMULARIO DE POSTULACIÓN</h2>
              <p style={{
                fontSize: 'var(--font-size-small)',
                fontFamily: 'var(--font-ui)',
                color: 'var(--color-text-muted)',
                textAlign: 'center',
                marginBottom: '2rem'
              }}>
                Completa el formulario y adjunta tu CV en formato PDF (máximo 2MB)
              </p>

              {error && (
                <div style={{
                  background: '#FEE2E2',
                  color: '#991B1B',
                  padding: '1rem',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: '1.5rem',
                  fontSize: 'var(--font-size-small)',
                  fontFamily: 'var(--font-ui)'
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--font-size-small)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: 'var(--color-text-primary)',
                    marginBottom: '0.5rem'
                  }}>Nombre Completo *</label>
                  <input
                    type="text"
                    required
                    value={formData.nombres}
                    onChange={(e) => setFormData({...formData, nombres: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1px solid var(--color-stroke)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)',
                      color: 'var(--color-text-primary)',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = 'none'
                      e.currentTarget.style.borderColor = 'var(--color-text-primary)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-stroke)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--font-size-small)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: 'var(--color-text-primary)',
                    marginBottom: '0.5rem'
                  }}>Teléfono</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    placeholder="Opcional"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1px solid var(--color-stroke)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)',
                      color: 'var(--color-text-primary)',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = 'none'
                      e.currentTarget.style.borderColor = 'var(--color-text-primary)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-stroke)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--font-size-small)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: 'var(--color-text-primary)',
                    marginBottom: '0.5rem'
                  }}>Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1px solid var(--color-stroke)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)',
                      color: 'var(--color-text-primary)',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = 'none'
                      e.currentTarget.style.borderColor = 'var(--color-text-primary)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-stroke)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--font-size-small)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: 'var(--color-text-primary)',
                    marginBottom: '0.5rem'
                  }}>Cargo al que Postulas *</label>
                  <input
                    type="text"
                    required
                    value={formData.cargo}
                    onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                    placeholder="Ej: Vendedor, Supervisor, Cajero, etc."
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1px solid var(--color-stroke)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)',
                      color: 'var(--color-text-primary)',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = 'none'
                      e.currentTarget.style.borderColor = 'var(--color-text-primary)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-stroke)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 'var(--font-size-small)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    color: 'var(--color-text-primary)',
                    marginBottom: '0.5rem'
                  }}>CV (PDF) *</label>
                  <input
                    id="cv-input"
                    type="file"
                    accept=".pdf,application/pdf"
                    required
                    onChange={handleFileChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1px solid var(--color-stroke)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-body)',
                      fontFamily: 'var(--font-ui)',
                      color: 'var(--color-text-primary)',
                      transition: 'border-color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = 'none'
                      e.currentTarget.style.borderColor = 'var(--color-text-primary)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-stroke)'
                    }}
                  />
                  <p style={{
                    marginTop: '0.5rem',
                    fontSize: 'var(--font-size-small)',
                    color: 'var(--color-text-muted)',
                    fontFamily: 'var(--font-ui)'
                  }}>
                    Solo archivos PDF. Tamaño máximo: 2MB
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.875rem 2rem',
                    background: loading ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--font-size-body)',
                    fontWeight: '500',
                    fontFamily: 'var(--font-ui)',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    letterSpacing: '0.02em'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.background = 'var(--brand-blue)'
                      e.currentTarget.style.color = 'white'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'none'
                      e.currentTarget.style.background = 'var(--color-text-primary)'
                      e.currentTarget.style.color = '#FFFFFF'
                    }
                  }}
                  onMouseDown={(e) => {
                    if (!loading) {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                      e.currentTarget.style.background = 'var(--brand-blue-600)'
                    }
                  }}
                >
                  {loading ? 'ENVIANDO...' : 'ENVIAR POSTULACIÓN'}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* FAQs Section */}
        {faqs.length > 0 && (
          <section id="faqs" role="region" aria-label="Preguntas Frecuentes" style={{ marginTop: '3rem', maxWidth: '800px', margin: '3rem auto 0' }}>
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--font-size-display-l)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-primary)',
              textAlign: 'center',
              marginBottom: '2rem'
            }}>Preguntas Frecuentes</h2>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {faqs.map((faq) => (
                <details key={faq.id} style={{
                  background: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.5rem',
                  border: '1px solid var(--color-stroke)',
                  cursor: 'pointer'
                }}>
                  <summary style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-h4)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--color-text-primary)',
                    listStyle: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    {faq.pregunta}
                    <span style={{ fontSize: '1.5rem', marginLeft: '1rem' }}>+</span>
                  </summary>
                  <p style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-text-muted)',
                    lineHeight: '1.6',
                    marginTop: '1rem',
                    whiteSpace: 'pre-line'
                  }}>
                    {faq.respuesta}
                  </p>
                </details>
              ))}
            </div>
            
            {/* FAQPage JSON-LD */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqs.map(faq => ({
                  "@type": "Question",
                  "name": faq.pregunta,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.respuesta
                  }
                }))
              })}
            </script>
          </section>
        )}
      </div>
    </>
  )
}
